/**
 * The widget Overview's layout rules, kept apart from the components so they
 * can be tested without mounting a widget.
 *
 * THE API DECIDES THE LAYOUT. GET /dashboard/layout already drops unknown keys,
 * coming-soon widgets, widgets for verticals the company lacks and widgets the
 * caller's role cannot see, and forces action_queue on and first. Everything
 * here is a second guard on that answer, never a second opinion about it:
 *
 *   - ORDER is the API's. Nothing here sorts by anything but `order`.
 *   - A key with no component is skipped, so a widget the API ships before this
 *     bundle knows it renders nothing rather than throwing.
 *   - VERTICALS are read off the catalogue the same response carries, not from
 *     a list kept here, so there is one vertical map (dashboard-widgets.ts in
 *     the API) and this cannot drift from it. A widget whose catalogue entry
 *     names verticals renders only when `hasVertical` holds for one of them —
 *     a positive guard, so a multi-vertical tenant is never excluded.
 *   - THE LOCKED WIDGET renders whatever `enabled` says. The API refuses a
 *     layout with it off, and the read forces it on; a payload that somehow
 *     says otherwise is a payload this screen does not believe.
 */
import type { DashboardCatalogueEntry, DashboardLayoutWidget, DashboardLayoutWrite } from '../types/dashboard-layout';

export const LOCKED_WIDGET_KEY = 'action_queue';

/** The widgets to draw, in the API's order. */
export function resolveOverviewWidgets(
  widgets: readonly DashboardLayoutWidget[],
  catalogue: readonly DashboardCatalogueEntry[],
  hasVertical: (key: string) => boolean,
  isRegistered: (key: string) => boolean,
): DashboardLayoutWidget[] {
  const verticalsByKey = new Map(catalogue.map((c) => [c.key, c.verticals]));
  return widgets
    .slice()
    .sort((a, b) => a.order - b.order)
    .filter((w) => {
      if (!isRegistered(w.key)) return false;
      const verticals = verticalsByKey.get(w.key);
      // Not in the catalogue: the API resolved a widget it does not list. Draw nothing.
      if (!verticals) return false;
      if (verticals.length > 0 && !verticals.some((v) => hasVertical(v))) return false;
      return w.locked || w.key === LOCKED_WIDGET_KEY || w.enabled;
    });
}

/* ────────────── the Settings picker ────────────── */

export interface PickerRow {
  key: string;
  label: string;
  enabled: boolean;
  locked: boolean;
  /** The catalogue's `is_new`, as sent. Only a delivered true is New. */
  isNew: boolean;
}

/**
 * The picker's rows: every AVAILABLE catalogue widget, in the saved order, with
 * the ones the layout does not carry appended (off) in catalogue order.
 *
 * The layout's `widgets` is role-filtered and the catalogue is not, so a widget
 * an admin can use but the layout omits is one that is off — the API only
 * stores what it was sent, and a fresh default carries only the default set.
 */
export function pickerRows(
  widgets: readonly DashboardLayoutWidget[],
  catalogue: readonly DashboardCatalogueEntry[],
): PickerRow[] {
  const available = catalogue.filter((c) => c.available);
  const availableKeys = new Set(available.map((c) => c.key));
  const isNew = new Set(available.filter((c) => c.is_new === true).map((c) => c.key));
  const rows: PickerRow[] = widgets
    .slice()
    .sort((a, b) => a.order - b.order)
    .filter((w) => availableKeys.has(w.key))
    .map((w) => ({ key: w.key, label: w.label, enabled: w.locked || w.enabled, locked: w.locked, isNew: isNew.has(w.key) }));
  const placed = new Set(rows.map((r) => r.key));
  for (const c of available) {
    if (!placed.has(c.key)) rows.push({ key: c.key, label: c.label, enabled: c.locked, locked: c.locked, isNew: isNew.has(c.key) });
  }
  // Locked first, always: the API refuses a layout where it is not.
  const lockedIdx = rows.findIndex((r) => r.locked);
  if (lockedIdx > 0) rows.unshift(rows.splice(lockedIdx, 1)[0]);
  return rows;
}

/** Move a row one place. The locked row never moves, and nothing moves above it. */
export function moveRow(rows: readonly PickerRow[], key: string, direction: -1 | 1): PickerRow[] {
  const i = rows.findIndex((r) => r.key === key);
  const j = i + direction;
  if (i < 0 || j < 0 || j >= rows.length) return rows.slice();
  if (rows[i].locked || rows[j].locked) return rows.slice();
  const next = rows.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/** The PUT body. Order is position; the locked row is forced on. */
export function layoutWrite(rows: readonly PickerRow[], version: number): DashboardLayoutWrite {
  return {
    version,
    widgets: rows.map((r, i) => ({ key: r.key, enabled: r.locked ? true : r.enabled, order: i + 1 })),
  };
}
